/**
 * Board Component
 *
 * Renders the 10×20 grid of squares. Applies player perspective (B sees board
 * flipped), wires click/hover to callbacks, and overlays selection/possible-move
 * state. Fully controlled; no game logic.
 */

import React, { useMemo } from 'react';
import { BoardSquare } from './BoardSquare.tsx';
import type { BoardProps } from '../../types/board.types';
import type { SquareData } from '../../types/board.types';
import type { Position } from '../../types/game.types';
import type { PlayerLetter, PlayerColor, MoveRecord } from '../../types/game.types';
import './Board.css';
import type { FrontendMonster } from '../../types/monster.types';

/** Extended board props for perspective and piece colors (optional). */
type BoardPropsExtended = BoardProps & {
    playerLetter?: PlayerLetter | null;
    playerAColor?: PlayerColor | null;
    playerBColor?: PlayerColor | null;
};

/**
 * Returns whether a position matches the given square (row/col).
 */
function positionMatches(pos: Position | null | undefined, square: SquareData): boolean {
    if (!pos) return false;
    return pos.row === square.row && pos.col === square.col;
}

function containsSelectedPiece(selectedPiece: FrontendMonster | null, square: SquareData): boolean {
    if (!selectedPiece) return false;
    if (square.type !== 'monster' || square.player == null || square.value == null) return false;
    // Match by monster identity on the displayed square, not selectedPiece.position,
    // so selection stays correct when viewing reconstructed history boards.
    return square.player === selectedPiece.player && square.value === selectedPiece.type;
}

/**
 * Returns whether (row, col) is in the possibleMoves list.
 */
function isPossibleMove(possibleMoves: Position[] | undefined, square: SquareData): boolean {
    if (!possibleMoves?.length) return false;
    return possibleMoves.some((p) => p.row === square.row && p.col === square.col);
}

function isHistorySquare(lastMove: MoveRecord | null | undefined, square: SquareData): boolean {
    if (!lastMove) return false;
    const isFrom = lastMove.from.row === square.row && lastMove.from.col === square.col;
    const isTo = lastMove.to.row === square.row && lastMove.to.col === square.col;
    return isFrom || isTo;
}

export const Board: React.FC<BoardPropsExtended> = ({
    squares,
    onSquareClick,
    onSquareHover,
    selectedSquare = null,
    selectedPiece = null,
    possibleMoves = [],
    disabled = false,
    phase: _phase,
    theme = 'ocean',
    lastMove = null,
    playerLetter,
    playerAColor = null,
    playerBColor = null,
}) => {
    // Apply player perspective: B sees the board reversed (their half at bottom in DOM).
    const displaySquares = useMemo(() => {
        if (playerLetter === 'A') {
            return [...squares].reverse();
        }
        return squares;
    }, [squares, playerLetter]);

    return (
        <div className="board_aspect_wrapper">
            <div className="board_container">
                {displaySquares.map((square) => (
                    <BoardSquare
                        key={`${square.row}-${square.col}`}
                        square={square}
                        onClick={onSquareClick}
                        onHover={onSquareHover}
                        selected={positionMatches(selectedSquare, square)}
                        selectedPiece={containsSelectedPiece(selectedPiece, square)}
                        history={isHistorySquare(lastMove, square)}
                        possible={isPossibleMove(possibleMoves, square)}
                        disabled={disabled}
                        theme={theme}
                        playerAColor={playerAColor}
                        playerBColor={playerBColor}
                        viewerPlayerLetter={playerLetter}
                    />
                ))}
                <div className="placement_line" aria-hidden />
            </div>
        </div>
    );
};

export default Board;
